/**
 * Embed Config Panel
 * Shared configuration component for Embed Widget Editor
 * Can be used in both the embed widget and the main app's /distribute page
 */

import React, { useCallback, useMemo, useState } from 'react';

import {
  AGENT_ICON_OPTIONS,
  BorderRadius,
  Brain,
  DEFAULT_CONFIG,
  EmbedConfig,
  EmbedType,
  THEME_PRESETS,
  ThemeMode,
  ThemePreset,
} from '../hooks/useEmbedEditor';

// Re-export types and constants for backwards compatibility
export type { BorderRadius, Brain, EmbedConfig, EmbedType, ThemeMode, ThemePreset };
export { DEFAULT_CONFIG, THEME_PRESETS };

export interface EmbedConfigPanelProps {
  config: EmbedConfig;
  onConfigChange: (config: EmbedConfig) => void;
  brains: Brain[];
  userHandle: string;
  userId: string;
  loading?: boolean;
  embedCode: string;
  onCopy: () => void;
  copied: boolean;
  // Theme styling (for embed context)
  theme?: {
    colors: {
      background: { surface: string; elevated: string; subtle: string; hover: string };
      text: { primary: string; secondary: string; tertiary: string; inverse: string };
      border: { default: string };
    };
    typography: {
      fontSize: Record<string, string>;
      fontWeight: Record<string, number>;
    };
    spacing: Record<string, string>;
    borderRadius: Record<string, string>;
    shadow: Record<string, string>;
  };
  primaryColor?: string;
  // Media URL helper
  appendMediaUrl?: (url: string) => string;
}

// ============================================================================
// Icons
// ============================================================================

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CopyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const BrainIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M12 2a9 9 0 0 0-9 9c0 3.6 2.4 6.6 5.7 7.7.3.1.5-.1.5-.4v-1.5c-2.3.5-2.8-1.1-2.8-1.1-.4-1-.9-1.3-.9-1.3-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.2 1.9.9 2.3.7.1-.5.3-.9.5-1.1-1.8-.2-3.7-.9-3.7-4 0-.9.3-1.6.8-2.2-.1-.2-.4-1 .1-2.1 0 0 .7-.2 2.2.8.6-.2 1.3-.3 2-.3s1.4.1 2 .3c1.5-1 2.2-.8 2.2-.8.4 1.1.2 1.9.1 2.1.5.6.8 1.3.8 2.2 0 3.1-1.9 3.8-3.7 4 .3.3.6.8.6 1.6v2.4c0 .3.2.5.5.4A9 9 0 0 0 12 2z" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// Main Component
// ============================================================================

export const EmbedConfigPanel: React.FC<EmbedConfigPanelProps> = ({
  config,
  onConfigChange,
  brains,
  userHandle,
  userId,
  loading = false,
  embedCode,
  onCopy,
  copied,
  theme,
  primaryColor: themePrimaryColor,
  appendMediaUrl = (url) => url,
}) => {
  const [showBrainPicker, setShowBrainPicker] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);

  // Update config field
  const updateConfig = useCallback(
    (field: keyof EmbedConfig, value: string | boolean) => {
      onConfigChange({ ...config, [field]: value });
    },
    [config, onConfigChange],
  );

  // Use inline styles when theme is provided (embed context)
  const useInlineStyles = !!theme;

  // Common styles for embed context
  const styles = useMemo(() => {
    if (!theme) return null;
    return {
      label: {
        display: 'block',
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.medium,
        color: theme.colors.text.primary,
        marginBottom: theme.spacing['2'],
      },
      input: {
        width: '100%',
        padding: `${theme.spacing['2']} ${theme.spacing['3']}`,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.background.surface,
        color: theme.colors.text.primary,
        fontSize: theme.typography.fontSize.sm,
      },
      button: {
        width: '100%',
        padding: `${theme.spacing['2.5']} ${theme.spacing['3']}`,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.background.surface,
        color: theme.colors.text.primary,
        fontSize: theme.typography.fontSize.sm,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        textAlign: 'left' as const,
      },
      section: {
        marginBottom: theme.spacing['5'],
      },
      card: {
        backgroundColor: theme.colors.background.surface,
        borderRadius: theme.borderRadius.xl,
        border: `1px solid ${theme.colors.border.default}`,
        padding: theme.spacing['4'],
      },
      modalOverlay: {
        position: 'fixed' as const,
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      },
      modalContent: {
        backgroundColor: theme.colors.background.surface,
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadow.xl,
        maxWidth: '400px',
        width: '100%',
        margin: theme.spacing['4'],
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column' as const,
      },
      modalHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing['4'],
        borderBottom: `1px solid ${theme.colors.border.default}`,
      },
      modalBody: {
        overflowY: 'auto' as const,
        flex: 1,
        padding: theme.spacing['2'],
      },
    };
  }, [theme]);

  // Render with Tailwind (for /distribute) or inline styles (for embed)
  if (useInlineStyles && styles) {
    return (
      <div style={styles.card}>
        {/* Embed Type */}
        <div style={styles.section}>
          <label htmlFor="embed-type" style={styles.label}>
            Embed Type
          </label>
          <select
            id="embed-type"
            value={config.type}
            onChange={(e) => updateConfig('type', e.target.value)}
            style={styles.input}
          >
            <option value="profile">Profile</option>
            <option value="brain">Brain</option>
          </select>
        </div>

        {/* Brain Selection */}
        {config.type === 'brain' && (
          <div style={styles.section}>
            <span style={styles.label}>Select Brain</span>
            <button
              type="button"
              onClick={() => setShowBrainPicker(true)}
              style={styles.button}
              aria-label="Select Brain"
            >
              <span
                style={{ color: config.brainId ? theme.colors.text.primary : theme.colors.text.tertiary }}
              >
                {brains.find((b) => b._id === config.brainId)?.name || 'Select a brain...'}
              </span>
              <ChevronDownIcon />
            </button>
          </div>
        )}

        {/* Profile Handle */}
        {config.type === 'profile' && (
          <div style={styles.section}>
            <label htmlFor="profile-handle" style={styles.label}>
              Profile Handle
            </label>
            <input
              id="profile-handle"
              type="text"
              value={config.profileHandle}
              readOnly
              style={{
                ...styles.input,
                backgroundColor: theme.colors.background.elevated,
                cursor: 'not-allowed',
              }}
            />
          </div>
        )}

        {/* Theme Selection */}
        <div style={styles.section}>
          <span style={styles.label}>Theme</span>
          <button
            type="button"
            onClick={() => setShowThemePicker(true)}
            style={styles.button}
            aria-label="Select Theme"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing['3'] }}>
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: theme.borderRadius.full,
                  backgroundColor: config.useCustomTheme
                    ? config.primaryColor
                    : THEME_PRESETS.find((p) => p.id === config.themePreset)?.color || '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${theme.colors.border.default}`,
                }}
              >
                {(config.useCustomTheme
                  ? config.customMode
                  : THEME_PRESETS.find((p) => p.id === config.themePreset)?.mode) === 'dark' && (
                  <span style={{ color: 'white', fontSize: '12px' }}>🌙</span>
                )}
              </div>
              <span
                style={{ color: theme.colors.text.primary, fontWeight: theme.typography.fontWeight.medium }}
              >
                {config.useCustomTheme
                  ? 'Custom Theme'
                  : THEME_PRESETS.find((p) => p.id === config.themePreset)?.name || 'Default'}
              </span>
            </div>
            <ChevronDownIcon />
          </button>
        </div>

        {/* Dimensions */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: theme.spacing['4'],
            marginBottom: theme.spacing['5'],
          }}
        >
          <div>
            <label htmlFor="embed-width" style={styles.label}>
              Width (px)
            </label>
            <input
              id="embed-width"
              type="number"
              value={config.width}
              onChange={(e) => updateConfig('width', e.target.value)}
              min="280"
              max="800"
              style={styles.input}
            />
          </div>
          <div>
            <label htmlFor="embed-height" style={styles.label}>
              Height (px)
            </label>
            <input
              id="embed-height"
              type="number"
              value={config.height}
              onChange={(e) => updateConfig('height', e.target.value)}
              min="400"
              max="900"
              style={styles.input}
            />
          </div>
        </div>

        {/* Guest Mode Toggle */}
        <div style={styles.section}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <span style={styles.label}>Guest Mode</span>
              <p
                style={{
                  margin: 0,
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.text.secondary,
                }}
              >
                Hide sign-in option for external sites
              </p>
            </div>
            <button
              type="button"
              onClick={() => updateConfig('guestMode', !config.guestMode)}
              style={{
                position: 'relative',
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: config.guestMode
                  ? themePrimaryColor || '#3b82f6'
                  : theme.colors.border.default,
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              aria-label="Toggle guest mode"
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: config.guestMode ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s',
                }}
              />
            </button>
          </div>
        </div>

        {/* Agent Icon Selection */}
        <div style={styles.section}>
          <span style={styles.label}>Agent Icon</span>
          <div
            style={{
              display: 'flex',
              gap: theme.spacing['2'],
            }}
          >
            {AGENT_ICON_OPTIONS.map((icon) => (
              <button
                key={icon.id}
                type="button"
                onClick={() => updateConfig('agentIcon', icon.id)}
                title={icon.description}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: theme.spacing['1.5'],
                  padding: theme.spacing['3'],
                  borderRadius: theme.borderRadius.lg,
                  border:
                    config.agentIcon === icon.id
                      ? `2px solid ${themePrimaryColor || '#3b82f6'}`
                      : `2px solid ${theme.colors.border.default}`,
                  backgroundColor:
                    config.agentIcon === icon.id ? theme.colors.background.elevated : 'transparent',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontSize: '20px',
                    color:
                      config.agentIcon === icon.id
                        ? themePrimaryColor || '#3b82f6'
                        : theme.colors.text.secondary,
                  }}
                >
                  {icon.id === 'robot' && '🤖'}
                  {icon.id === 'sparkle' && '✨'}
                  {icon.id === 'brain' && '🧠'}
                  {icon.id === 'chat' && '💬'}
                  {icon.id === 'message' && '📨'}
                </span>
                <span
                  style={{
                    fontSize: theme.typography.fontSize.xs,
                    fontWeight: theme.typography.fontWeight.medium,
                    color:
                      config.agentIcon === icon.id
                        ? themePrimaryColor || '#3b82f6'
                        : theme.colors.text.secondary,
                  }}
                >
                  {icon.name}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Embed Code */}
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: theme.spacing['2'],
            }}
          >
            <span style={{ ...styles.label, marginBottom: 0 }}>Embed Code</span>
            <button
              onClick={onCopy}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing['1.5'],
                padding: `${theme.spacing['1.5']} ${theme.spacing['3']}`,
                backgroundColor: 'transparent',
                color: themePrimaryColor || '#3b82f6',
                border: 'none',
                borderRadius: theme.borderRadius.lg,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                cursor: 'pointer',
              }}
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <pre
            style={{
              backgroundColor: '#1f2937',
              color: '#f3f4f6',
              padding: theme.spacing['4'],
              borderRadius: theme.borderRadius.lg,
              fontSize: theme.typography.fontSize.xs,
              overflowX: 'auto',
              maxHeight: '200px',
            }}
          >
            <code>{embedCode}</code>
          </pre>
        </div>

        {/* Brain Picker Modal */}
        {showBrainPicker && (
          <div style={styles.modalOverlay} onClick={() => setShowBrainPicker(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3
                  style={{
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.text.primary,
                    margin: 0,
                  }}
                >
                  Select Brain
                </h3>
                <button
                  onClick={() => setShowBrainPicker(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: theme.spacing['1'],
                  }}
                >
                  <XIcon />
                </button>
              </div>
              <div style={styles.modalBody}>
                {brains.length === 0 ? (
                  <p
                    style={{
                      textAlign: 'center',
                      color: theme.colors.text.secondary,
                      padding: theme.spacing['6'],
                    }}
                  >
                    No brains found
                  </p>
                ) : (
                  brains.map((brain) => (
                    <button
                      key={brain._id}
                      onClick={() => {
                        updateConfig('brainId', brain._id);
                        setShowBrainPicker(false);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing['3'],
                        padding: theme.spacing['3'],
                        borderRadius: theme.borderRadius.lg,
                        border:
                          config.brainId === brain._id
                            ? `2px solid ${themePrimaryColor || '#3b82f6'}`
                            : '2px solid transparent',
                        backgroundColor:
                          config.brainId === brain._id ? theme.colors.background.elevated : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        marginBottom: theme.spacing['1'],
                      }}
                    >
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: theme.borderRadius.full,
                          backgroundColor: '#9333ea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BrainIcon className="text-white" />
                      </div>
                      <span
                        style={{
                          flex: 1,
                          color: theme.colors.text.primary,
                          fontWeight: theme.typography.fontWeight.medium,
                        }}
                      >
                        {brain.name}
                      </span>
                      {config.brainId === brain._id && <CheckIcon />}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Theme Picker Modal */}
        {showThemePicker && (
          <div style={styles.modalOverlay} onClick={() => setShowThemePicker(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3
                  style={{
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.semibold,
                    color: theme.colors.text.primary,
                    margin: 0,
                  }}
                >
                  Select Theme
                </h3>
                <button
                  onClick={() => setShowThemePicker(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: theme.spacing['1'],
                  }}
                >
                  <XIcon />
                </button>
              </div>
              <div style={styles.modalBody}>
                {/* Preset Themes */}
                {THEME_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => {
                      onConfigChange({ ...config, themePreset: preset.id, useCustomTheme: false });
                      setShowThemePicker(false);
                    }}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing['3'],
                      padding: theme.spacing['3'],
                      borderRadius: theme.borderRadius.lg,
                      border:
                        !config.useCustomTheme && config.themePreset === preset.id
                          ? `2px solid ${themePrimaryColor || '#3b82f6'}`
                          : '2px solid transparent',
                      backgroundColor:
                        !config.useCustomTheme && config.themePreset === preset.id
                          ? theme.colors.background.elevated
                          : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      marginBottom: theme.spacing['1'],
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: theme.borderRadius.full,
                        backgroundColor: preset.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${theme.colors.border.default}`,
                      }}
                    >
                      {preset.mode === 'dark' && <span style={{ color: 'white', fontSize: '14px' }}>🌙</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          color: theme.colors.text.primary,
                          fontWeight: theme.typography.fontWeight.medium,
                        }}
                      >
                        {preset.name}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: theme.typography.fontSize.xs,
                          color: theme.colors.text.secondary,
                        }}
                      >
                        {preset.mode === 'dark' ? 'Dark mode' : 'Light mode'}
                      </p>
                    </div>
                    {!config.useCustomTheme && config.themePreset === preset.id && <CheckIcon />}
                  </button>
                ))}

                {/* Custom Theme Option */}
                <div
                  style={{
                    borderTop: `1px solid ${theme.colors.border.default}`,
                    marginTop: theme.spacing['2'],
                    paddingTop: theme.spacing['2'],
                  }}
                >
                  <button
                    onClick={() => onConfigChange({ ...config, useCustomTheme: true })}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: theme.spacing['3'],
                      padding: theme.spacing['3'],
                      borderRadius: theme.borderRadius.lg,
                      border: config.useCustomTheme
                        ? `2px solid ${themePrimaryColor || '#3b82f6'}`
                        : '2px solid transparent',
                      backgroundColor: config.useCustomTheme
                        ? theme.colors.background.elevated
                        : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: theme.borderRadius.full,
                        border: `2px dashed ${theme.colors.border.default}`,
                        backgroundColor: config.useCustomTheme ? config.primaryColor : undefined,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {config.useCustomTheme ? (
                        <span style={{ color: 'white', fontSize: '14px' }}>
                          {config.customMode === 'dark' ? '🌙' : '☀️'}
                        </span>
                      ) : (
                        <span style={{ color: theme.colors.text.tertiary, fontSize: '18px' }}>+</span>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          color: theme.colors.text.primary,
                          fontWeight: theme.typography.fontWeight.medium,
                        }}
                      >
                        Custom Theme
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: theme.typography.fontSize.xs,
                          color: theme.colors.text.secondary,
                        }}
                      >
                        Create your own theme
                      </p>
                    </div>
                    {config.useCustomTheme && <CheckIcon />}
                  </button>

                  {/* Custom Theme Options */}
                  {config.useCustomTheme && (
                    <div
                      style={{
                        marginTop: theme.spacing['3'],
                        padding: theme.spacing['4'],
                        backgroundColor: theme.colors.background.subtle,
                        borderRadius: theme.borderRadius.lg,
                        border: `1px solid ${theme.colors.border.default}`,
                      }}
                    >
                      {/* Mode */}
                      <div style={{ marginBottom: theme.spacing['4'] }}>
                        <span style={{ ...styles.label, color: theme.colors.text.secondary }}>Mode</span>
                        <div
                          style={{ display: 'flex', gap: theme.spacing['2'] }}
                          role="group"
                          aria-label="Theme mode"
                        >
                          <button
                            onClick={() => updateConfig('customMode', 'light')}
                            style={{
                              flex: 1,
                              padding: `${theme.spacing['1.5']} ${theme.spacing['3']}`,
                              borderRadius: theme.borderRadius.lg,
                              border:
                                config.customMode === 'light'
                                  ? `1px solid ${themePrimaryColor || '#3b82f6'}`
                                  : `1px solid ${theme.colors.border.default}`,
                              backgroundColor:
                                config.customMode === 'light'
                                  ? theme.colors.background.surface
                                  : 'transparent',
                              color:
                                config.customMode === 'light'
                                  ? themePrimaryColor || '#3b82f6'
                                  : theme.colors.text.primary,
                              fontSize: theme.typography.fontSize.sm,
                              cursor: 'pointer',
                            }}
                          >
                            ☀️ Light
                          </button>
                          <button
                            onClick={() => updateConfig('customMode', 'dark')}
                            style={{
                              flex: 1,
                              padding: `${theme.spacing['1.5']} ${theme.spacing['3']}`,
                              borderRadius: theme.borderRadius.lg,
                              border:
                                config.customMode === 'dark'
                                  ? `1px solid ${themePrimaryColor || '#3b82f6'}`
                                  : `1px solid ${theme.colors.border.default}`,
                              backgroundColor:
                                config.customMode === 'dark'
                                  ? theme.colors.background.surface
                                  : 'transparent',
                              color:
                                config.customMode === 'dark'
                                  ? themePrimaryColor || '#3b82f6'
                                  : theme.colors.text.primary,
                              fontSize: theme.typography.fontSize.sm,
                              cursor: 'pointer',
                            }}
                          >
                            🌙 Dark
                          </button>
                        </div>
                      </div>

                      {/* Primary Color */}
                      <div style={{ marginBottom: theme.spacing['4'] }}>
                        <label
                          htmlFor="primary-color-text"
                          style={{ ...styles.label, color: theme.colors.text.secondary }}
                        >
                          Primary Color
                        </label>
                        <div style={{ display: 'flex', gap: theme.spacing['2'], alignItems: 'center' }}>
                          <input
                            type="color"
                            value={config.primaryColor}
                            onChange={(e) => updateConfig('primaryColor', e.target.value)}
                            aria-label="Primary color picker"
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: theme.borderRadius.md,
                              cursor: 'pointer',
                              border: `1px solid ${theme.colors.border.default}`,
                            }}
                          />
                          <input
                            id="primary-color-text"
                            type="text"
                            value={config.primaryColor}
                            onChange={(e) => updateConfig('primaryColor', e.target.value)}
                            style={{ ...styles.input, flex: 1 }}
                          />
                        </div>
                      </div>

                      {/* Border Radius */}
                      <div style={{ marginBottom: theme.spacing['4'] }}>
                        <span style={{ ...styles.label, color: theme.colors.text.secondary }}>
                          Border Radius
                        </span>
                        <div
                          style={{ display: 'flex', gap: theme.spacing['1'] }}
                          role="group"
                          aria-label="Border radius"
                        >
                          {(['none', 'sm', 'md', 'lg', 'full'] as BorderRadius[]).map((radius) => (
                            <button
                              key={radius}
                              onClick={() => updateConfig('borderRadius', radius)}
                              style={{
                                flex: 1,
                                padding: `${theme.spacing['1.5']} ${theme.spacing['2']}`,
                                borderRadius: theme.borderRadius.md,
                                border:
                                  config.borderRadius === radius
                                    ? `1px solid ${themePrimaryColor || '#3b82f6'}`
                                    : `1px solid ${theme.colors.border.default}`,
                                backgroundColor:
                                  config.borderRadius === radius
                                    ? theme.colors.background.surface
                                    : 'transparent',
                                color:
                                  config.borderRadius === radius
                                    ? themePrimaryColor || '#3b82f6'
                                    : theme.colors.text.secondary,
                                fontSize: theme.typography.fontSize.xs,
                                cursor: 'pointer',
                              }}
                            >
                              {radius}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Done button */}
                      <button
                        onClick={() => setShowThemePicker(false)}
                        style={{
                          width: '100%',
                          padding: `${theme.spacing['2']} ${theme.spacing['4']}`,
                          backgroundColor: themePrimaryColor || '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: theme.borderRadius.lg,
                          fontSize: theme.typography.fontSize.sm,
                          fontWeight: theme.typography.fontWeight.medium,
                          cursor: 'pointer',
                        }}
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Return null for Tailwind version - will be handled by Distribute.tsx directly
  return null;
};

export default EmbedConfigPanel;
